import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Sparkles, Target, Trophy, Users, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Welcome = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Target, title: "Goal Tracking", desc: "Set and achieve your learning goals" },
    { icon: Trophy, title: "Achievements", desc: "Earn badges and track progress" },
    { icon: Users, title: "Study Groups", desc: "Collaborate with peers" },
    { icon: TrendingUp, title: "Progress Analytics", desc: "Visualize your journey" },
  ];

  const premiumFeatures = [
    "1-on-1 mentoring sessions",
    "Exclusive masterclasses",
    "Advanced analytics dashboard",
    "Priority support",
    "Downloadable resources",
    "Certificate of completion",
    "Lifetime course access",
    "Weekly live Q&A sessions",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Welcome to Your Learning Journey</span>
          </div>
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Start Your Transformation Today
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join thousands of learners who are achieving their goals with our comprehensive platform
          </p>
        </div>

        {/* Free Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
              <feature.icon className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-semibold mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.desc}</p>
            </Card>
          ))}
        </div>

        {/* Premium CTA Section */}
        <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Unlock Premium Features</h2>
              <p className="text-muted-foreground mb-6">
                Get full access to all courses, mentoring, and exclusive content
              </p>
              <div className="space-y-3 mb-8">
                {premiumFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => navigate("/")}>
                  View Pricing Plans
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/dashboard")}>
                  Explore Dashboard
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <div className="text-center">
                  <Trophy className="h-24 w-24 text-primary mx-auto mb-4" />
                  <p className="text-2xl font-bold">Start Your Journey</p>
                  <p className="text-muted-foreground">Upgrade to Premium</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Social Proof */}
        <div className="text-center mt-16">
          <p className="text-sm text-muted-foreground mb-4">Trusted by 10,000+ learners worldwide</p>
          <div className="flex justify-center gap-8 text-sm">
            <div>
              <p className="text-2xl font-bold text-primary">4.9/5</p>
              <p className="text-muted-foreground">Average Rating</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">95%</p>
              <p className="text-muted-foreground">Success Rate</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">1000+</p>
              <p className="text-muted-foreground">Hours of Content</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;